import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { NamedLink } from '../../../components';
import { getOrderCardData } from '../orderCardData';

import css from './OrderCards.module.css';

// A fixed timezone, so the server render and the browser show the same date
const shortDate = (intl, date) =>
  intl.formatDate(date, { month: 'short', day: 'numeric', timeZone: 'America/New_York' });

const OrderPhotos = ({ images }) => (
  <span className={css.photos} data-count={Math.max(images.length, 1)} aria-hidden="true">
    {images.length > 0 ? (
      images.map((src, i) => <img key={`${src}-${i}`} className={css.photo} src={src} alt="" />)
    ) : (
      <span className={classNames(css.photo, css.noPhoto)} />
    )}
  </span>
);

const OrderCard = ({ card }) => {
  const intl = useIntl();
  const params = { id: card.id };
  const amount = intl.formatNumber(card.amount / 100, {
    style: 'currency',
    currency: card.currency,
  });

  return (
    <li className={css.card}>
      <OrderPhotos images={card.images} />
      <div className={css.body}>
        <div className={css.topRow}>
          <NamedLink className={css.cardLink} name={card.routeName} params={params}>
            <span className={css.title}>{card.title}</span>
            {card.moreCount > 0 ? (
              <span className={css.more}>
                <FormattedMessage id="OrderList.moreItems" values={{ count: card.moreCount }} />
              </span>
            ) : null}
          </NamedLink>
          <span className={css.amount}>{amount}</span>
        </div>
        <div className={css.meta}>
          {card.itemCount > 1 ? (
            <span>
              <FormattedMessage id="OrderList.itemCount" values={{ count: card.itemCount }} />
            </span>
          ) : null}
          {card.otherPartyName ? <span>{card.otherPartyName}</span> : null}
          {card.paidAt ? (
            <span>
              <FormattedMessage
                id="OrderList.paidOn"
                values={{ date: shortDate(intl, card.paidAt) }}
              />
            </span>
          ) : null}
        </div>
        <div className={css.bottomRow}>
          <span className={classNames(css.status, { [css.statusLate]: card.isLate })}>
            {card.statusLabel}
          </span>
          {card.action === 'ship' ? (
            <NamedLink
              className={css.action}
              name={card.routeName}
              params={params}
              to={{ hash: '#ship' }}
            >
              <FormattedMessage id="OrderList.shipOrder" />
            </NamedLink>
          ) : null}
          {card.action === 'confirmArrival' ? (
            <NamedLink className={css.actionSecondary} name={card.routeName} params={params}>
              <FormattedMessage id="OrderList.confirmArrival" />
            </NamedLink>
          ) : null}
        </div>
      </div>
    </li>
  );
};

/** Orders as cards: photos, item and buyer/seller, plain status and the one next step. */
const OrderCards = props => {
  const { transactions, currentUser } = props;
  const intl = useIntl();
  const cards = transactions.map(tx => getOrderCardData(tx, currentUser, intl));
  return (
    <ul className={css.root}>
      {cards.map(card => (
        <OrderCard key={card.id} card={card} />
      ))}
    </ul>
  );
};

export default OrderCards;
